import { useMemo, useState } from 'react';
import { Empty, Modal, Radio, Row } from 'antd';

import { BUTTON_STYLE } from '../../../../constants';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { useCapturePostHog } from '../../../../hooks/useCapturePostHog';
import type { BarChartOption } from '../../../../models/echarts';
import { ReactECharts } from '../../../charts/ReactECharts';
import { closeDashboardModal, DASHBOARD_MODAL } from './modalDashboardSlice';

const getOption = (data: [number, number, number][], weekPeriod: 'weekdays' | 'weekend', filtersInfo: string) => {
  if (!(data && data.length > 0 && data[0] !== null)) {
    return;
  }

  const graphData = data.reduce(
    (acc, item) => {
      const [day, hour, value] = item;
      const hourStr = hour < 12 ? `${hour === 0 ? 12 : hour}AM` : `${hour === 12 ? 12 : hour - 12}PM`;
      if (day === 0 || day === 6) {
        acc.weekend.set(hourStr, (acc.weekend.get(hourStr) || 0) + value);
      } else {
        acc.weekdays.set(hourStr, (acc.weekdays.get(hourStr) || 0) + value);
      }
      return acc;
    },
    { weekdays: new Map<string, number>(), weekend: new Map<string, number>() },
  );

  const option: BarChartOption = {
    title: {
      text: 'Client check-in hourly distribution',
      textStyle: {
        fontSize: 12,
      },
      subtext: filtersInfo,
    },
    tooltip: {
      trigger: 'axis',
      padding: [10, 10],
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
    },
    legend: {
      show: false,
    },
    color: ['#1890FF'],
    xAxis: {
      axisLabel: {
        interval: 0,
        fontSize: 11,
      },
      data: [...graphData.weekdays.keys()],
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 11,
      },
    },
    series: [{ type: 'bar', data: [...graphData[weekPeriod].values()] }],
  };

  return option;
};

export const CheckInDayAndTimeModal = ({
  data,
  filtersInfo,
}: {
  data: [number, number, number][];
  filtersInfo: string;
}) => {
  const dispatch = useAppDispatch();
  const { capturePostHogCustomEvent } = useCapturePostHog();
  const open = useAppSelector((state) => state.modalDashboardSlice.modal === DASHBOARD_MODAL.CHECKIN_DAY_AND_TIME);
  const [weekPeriod, setWeekPeriod] = useState<'weekdays' | 'weekend'>('weekdays');
  const option = useMemo(() => getOption(data, weekPeriod, filtersInfo), [data, filtersInfo, weekPeriod]);

  return (
    <Modal
      className="dashboardModal"
      width="60%"
      title="Check-in day and time details"
      open={open}
      onCancel={() => {
        capturePostHogCustomEvent('CheckInDayAndTimeModal close modal');
        dispatch(closeDashboardModal());
      }}
      mask={{ blur: false, closable: false }}
      footer={null}
    >
      <Row justify="end">
        <Radio.Group value={weekPeriod} onChange={(e) => setWeekPeriod(e.target.value)}>
          <Radio.Button
            style={weekPeriod === 'weekdays' ? BUTTON_STYLE.primary : BUTTON_STYLE.neutral}
            value="weekdays"
          >
            Weekdays
          </Radio.Button>
          <Radio.Button style={weekPeriod === 'weekend' ? BUTTON_STYLE.primary : BUTTON_STYLE.neutral} value="weekend">
            Weekend
          </Radio.Button>
        </Radio.Group>
      </Row>
      {option ? (
        <ReactECharts option={option} style={{ padding: 0, height: 600, marginTop: 0, minHeight: 500 }} />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 20 }} />
      )}
    </Modal>
  );
};
